/**
 * ปุ่ม "Continue with Facebook" — the words of the banners after Meta sends the owner back, the channel rows'
 * "ที่มาของสิทธิ์", and which pages the Meta reviewer may open (mockup artifact 7yGQ7Pjk69TWEiodkiANmE, owner 5 พ.ย. 69).
 * Pure: components/config/MetaConnectCard.vue + ChannelsSection.vue render them, App.vue guards the routes.
 */
import type { ChannelAccount, MetaGrantRow } from '../api/dash/types'
import type { ConnectPage, ConnectResult } from '../api/metaConnect'
import { clockTime, shortDate } from './inbox'

export const REQUIRED_COUNT = 10

export type BannerTone = 'ok' | 'warn' | 'bad' | 'plain'

export interface Banner {
  key: 'ok' | 'new' | 'missing' | 'cancel' | 'error' | 'reviewer'
  tone: BannerTone
  title: string
  lines: string[]
  note: string | null
  /** status new_pages: the Pages the owner may turn on from the banner */
  pages: ConnectPage[]
  /** "Continue with Facebook" again inside the banner (missing permissions) */
  retry: boolean
}

// The link may be used because the first try DID save (the owner reloaded after a slow answer): never "nothing changed"
const STATE_FAILED = 'ลิงก์เชื่อมหมดอายุ ใช้ไปแล้ว หรือเปิดจากเครื่องอื่น · ดูที่มาของสิทธิ์ในรายการด้านล่าง ถ้ายังไม่เปลี่ยน กดเชื่อมใหม่อีกครั้ง'
const ID = /^(fail|mock-[a-z_]+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/

/** `?meta_connect=` of the address: a result id, 'fail' (the server had nothing to show), or null (absent / junk). */
export function takeConnectId(search: string): string | null {
  const v = new URLSearchParams(search).get('meta_connect')
  return v && ID.test(v) ? v : null
}

const blank = { lines: [] as string[], note: null, pages: [] as ConnectPage[], retry: false }

export function failBanner(message: string | null = null): Banner {
  return { ...blank, key: 'error', tone: 'bad', title: 'เชื่อมไม่สำเร็จ', note: message ?? STATE_FAILED }
}

/** The result could not be read (network / server): what happened is not known here — the list below is. */
export function unknownBanner(): Banner {
  return { ...blank, key: 'error', tone: 'warn', title: 'ยังโหลดผลการเชื่อมไม่ได้', note: 'ดูที่มาของสิทธิ์ในรายการด้านล่าง ถ้ายังไม่เปลี่ยน กดเชื่อมใหม่อีกครั้ง' }
}

/** A linked Instagram account not ticked in Meta's dialog: named, and what it uses now (never the Page's new token). */
function skippedIgLines(p: ConnectPage): string[] {
  const s = p.ig_skipped
  if (!s) return []
  const who = s.username ? `Instagram @${s.username}` : `Instagram ที่ผูกกับเพจ ${p.name ?? p.page_id}`
  const now = s.env ? 'ยังใช้ค่าตั้งเซิร์ฟเวอร์เดิม' : 'ตอบจากระบบนี้ไม่ได้ จนกว่าจะกดเชื่อมใหม่แล้วเลือกบัญชีนี้'
  return [`${who} ไม่ได้เลือกในหน้าของ Meta · ${now}`]
}

export function bannersOf(r: ConnectResult): Banner[] {
  switch (r.status) {
    case 'ok':
    case 'new_pages': {
      const out: Banner[] = []
      const on = r.pages.filter((p) => p.active)
      const off = r.pages.filter((p) => !p.active)
      if (on.length) {
        const lines: string[] = []
        for (const p of on) {
          const rx = p.subscribed === false ? 'สมัครรับข้อความกับ Meta ไม่สำเร็จ กดเชื่อมใหม่อีกครั้ง' : 'รับข้อความเข้าระบบนี้แล้ว'
          lines.push(`เพจ ${p.name ?? p.page_id} · สิทธิ์ครบ ${REQUIRED_COUNT} · ${rx}`)
          if (p.ig_username) lines.push(`Instagram @${p.ig_username} · ผูกกับเพจด้านบน · สิทธิ์ครบ`)
          lines.push(...skippedIgLines(p))
        }
        const count = on.length + on.filter((p) => p.ig_username).length
        out.push({ ...blank, key: 'ok', tone: 'ok', title: `เชื่อมแล้ว · บันทึกสิทธิ์ใหม่ของ ${count} ช่องทาง`, lines })
      }
      if (off.length) {
        out.push({
          ...blank,
          key: 'new',
          tone: 'warn',
          title: `เพจใหม่ ${off.length} เพจ · บันทึกสิทธิ์ไว้แล้ว ยังไม่รับข้อความ`,
          lines: off.flatMap(skippedIgLines),
          note: `${off.length > 1 ? 'เพจเหล่านี้' : 'เพจนี้'}ยังไม่เคยต่อกับแชทรวม ระบบเก็บสิทธิ์ไว้ แต่ยังไม่สมัครรับข้อความและยังไม่ส่งอะไรออก จนกว่าเจ้าของจะกดเปิด`,
          pages: off,
        })
      }
      return out
    }
    case 'missing':
      return [{
        ...blank,
        key: 'missing',
        tone: 'bad',
        title: `ยังไม่ได้บันทึก · สิทธิ์ไม่ครบ ${r.missing.length} ตัว`,
        lines: r.missing.map((m) => `${m.label} (${m.scope})`),
        note: 'ถ้าบันทึก ฟีเจอร์เหล่านี้จะใช้ไม่ได้ ระบบจึงใช้สิทธิ์ชุดเดิมต่อ ไม่มีอะไรเปลี่ยน กดเชื่อมใหม่แล้วติ๊กให้ครบ',
        retry: true,
      }]
    case 'cancel':
      return [{ ...blank, key: 'cancel', tone: 'plain', title: 'ยกเลิกการเชื่อมแล้ว', note: 'ปิดหน้าต่าง Meta ก่อนกด Save ไม่มีอะไรเปลี่ยน' }]
    case 'reviewer':
      return [{
        ...blank,
        key: 'reviewer',
        tone: 'plain',
        title: 'ทดสอบสำเร็จ · ไม่มีอะไรเปลี่ยน',
        lines: r.missing.length ? [`ยังไม่ได้ให้สิทธิ์ ${r.missing.length} ตัว: ${r.missing.map((m) => m.scope).join(', ')}`] : [],
        note: `เข้าสู่ระบบ Meta ในชื่อ ${r.meta_name ?? 'บัญชี Meta'} · ให้สิทธิ์ ${r.scope_count} ตัว · เลือกเพจ ${r.page_count} เพจ บัญชีนี้ใช้ทดสอบ ระบบจึงไม่เก็บ token`,
      }]
    default:
      return [failBanner(r.message)]
  }
}

export const ENV_SOURCE = 'ค่าตั้งเซิร์ฟเวอร์ (ออกเองใน Graph API Explorer)'
const when = (iso: string): string => `${shortDate(iso)} ${clockTime(iso)}`

/** "ที่มาของสิทธิ์" of a channel row (mockup screens 1 + 3), or null when the row has no Meta token. */
export function grantText(a: Pick<ChannelAccount, 'grant_source' | 'granted_at' | 'granted_by_name'>): string | null {
  if (a.grant_source === 'env') return ENV_SOURCE
  if (a.grant_source !== 'button' || !a.granted_at) return null
  return ['ปุ่มเชื่อม Facebook', when(a.granted_at), a.granted_by_name].filter(Boolean).join(' · ')
}

/** "ต้องต่ออายุก่อน" for a system-user grant, else null (the row keeps showing Meta's data-access date). */
export function renewText(a: Pick<ChannelAccount, 'grant_source' | 'token_type'>): string | null {
  return a.grant_source === 'button' && a.token_type === 'system_user' ? 'ไม่ต้องต่ออายุ' : null
}

/**
 * The Instagram account that goes with a Page when the Page's button grant is dropped (one grant covers the Page and its
 * linked IG: DELETE /pages/{page_id} removes both) — "Instagram @name" for the question and the result line, or null.
 * `igUsername` = the grant's own (a kept Page row); else the IG channel row whose grant is this Page's.
 */
export function linkedIgLabel(
  items: Pick<ChannelAccount, 'channel' | 'name' | 'grant_page_id'>[],
  pageId: string,
  igUsername: string | null = null,
): string | null {
  if (igUsername) return `Instagram @${igUsername}`
  const ig = items.find((a) => a.channel === 'ig' && a.grant_page_id === pageId)
  if (!ig) return null
  const n = ig.name.trim()
  return /instagram/i.test(n) ? n : `Instagram ${n}`
}

export function newGrantLine(g: MetaGrantRow): string {
  const ig = g.ig_username ? ` · IG @${g.ig_username}` : ''
  return `บันทึกสิทธิ์แล้ว ${when(g.granted_at)}${ig} · ${g.active ? 'ยังไม่มีลูกค้าทัก' : 'ยังไม่รับข้อความ'}`
}

/** Pages a Meta reviewer never gets (their data 404s on the server anyway) — settings: ช่องทางที่เชื่อม only. */
export const REVIEWER_OFF = new Set(['dash', 'dash-b', 'knowledge', 'health', 'ads', 'posts', 'affiliate'])

export function reviewerPageAllowed(name: string, section: unknown): boolean {
  if (REVIEWER_OFF.has(name)) return false
  if (name !== 'config') return true
  const s = Array.isArray(section) ? section[0] : section
  return s === undefined || s === null || s === '' || s === 'channels'
}

let go: (url: string) => void = (url) => window.location.assign(url)

/** Leave for Meta's dialog in the same tab (tests replace it). */
export function navigate(url: string): void {
  go(url)
}

export function setNavigate(fn: ((url: string) => void) | null): void {
  go = fn ?? ((url) => window.location.assign(url))
}
